import { ArrowRight, LoaderCircle } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'

import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { type MetalTier } from 'components/prospects/types'
import { type QuoteGenerationOptions } from 'components/quoting/types'
import { Banner, Button, Checkbox, CheckboxGroup, FiltersDropdown, TextInput } from 'slab'
import { usePostQuotingProspectsProspectIdQuotesIdGenerate as useGenerateQuote } from 'utils/web_api'

export type CustomizeBenchmarksDropdownProps = {
  prospectId: string
  quoteId: string
  quoteGenerationOptions: Pick<QuoteGenerationOptions, 'metal_tiers' | 'coverage_percentiles' | 'coverage_start_date'>
  trigger: React.ReactNode | ((props: { open: boolean }) => React.ReactNode)
  onSuccess?: () => void
  align?: 'start' | 'end'
  preserveInitialValues?: boolean
}

const METAL_TIER_OPTIONS = [
  {
    label: 'Bronze',
    value: 'bronze',
  },
  {
    label: 'Silver',
    value: 'silver',
  },
  {
    label: 'Gold',
    value: 'gold',
  },
  {
    label: 'Platinum',
    value: 'platinum',
  },
]

const LoadingIndicator = () => (
  <LoaderCircle className='animate-spin' />
)

export default function CustomizeBenchmarksDropdown ({
  prospectId,
  quoteId,
  quoteGenerationOptions,
  trigger,
  onSuccess,
  align = 'start',
  preserveInitialValues = false,
}: CustomizeBenchmarksDropdownProps) {
  const initialMetalTiers = useRef(quoteGenerationOptions.metal_tiers)
  const initialCoveragePercentiles = useRef(quoteGenerationOptions.coverage_percentiles.map(Number))

  const [open, setOpen] = useState(false)
  const [selectedMetalTiers, setSelectedMetalTiers] = useState(quoteGenerationOptions.metal_tiers)
  const [selectedCoveragePercentiles, setSelectedCoveragePercentiles] = useState<Set<number>>(new Set())
  const [coveragePercentilesText, setCoveragePercentilesText] = useState('')
  const [error, setError] = useState<GrapeAPIErrorType>()

  useEffect(() => {
    setSelectedCoveragePercentiles(new Set(quoteGenerationOptions.coverage_percentiles.map(Number)))
    setCoveragePercentilesText(quoteGenerationOptions.coverage_percentiles.sort((a, b) => Number(a) - Number(b)).join(','))
  }, [quoteGenerationOptions.coverage_percentiles])

  const generateQuoteMutation = useGenerateQuote()

  const onClickNext = () => {
    const metalTiersToSubmit = preserveInitialValues
      ? Array.from(new Set([...initialMetalTiers.current, ...selectedMetalTiers]))
      : selectedMetalTiers

    const coveragePercentilestoSubmit = preserveInitialValues
      ? Array.from(new Set([...initialCoveragePercentiles.current, ...Array.from(selectedCoveragePercentiles)]))
      : Array.from(selectedCoveragePercentiles)

    generateQuoteMutation.mutate({
      id: quoteId,
      prospectId,
      data: {
        coverage_start_date: quoteGenerationOptions.coverage_start_date,
        metal_tiers: metalTiersToSubmit,
        coverage_percentiles: coveragePercentilestoSubmit,
      },
    }, {
      onSuccess: () => {
        onSuccess?.()
        setOpen(false)
      },
      onError: (error) => {
        setError(error as GrapeAPIErrorType)
      },
    })
  }

  const areCoveragePercentilesValid = (percentilesText: string) => {
    const cleanText = percentilesText.replace(/,+$/, '')
    const percentiles = cleanText.split(',').map(p => p.trim()).filter(Boolean)
    return percentiles.every(p => {
      const num = Number(p)
      return !isNaN(num) && num >= 1 && num <= 100
    })
  }

  const handleCoveragePercentilesChange = (value: string) => {
    setCoveragePercentilesText(value)
    if (areCoveragePercentilesValid(value)) {
      const cleanText = value.replace(/,+$/, '')
      const percentiles = cleanText.split(',').map(p => Number(p.trim())).filter(Boolean)
      setSelectedCoveragePercentiles(new Set(percentiles))
    }
  }

  const onChangeMetalTier = (metalTier: MetalTier) => {
    if (selectedMetalTiers.includes(metalTier)) {
      setSelectedMetalTiers((prev) => prev.filter((tier) => tier !== metalTier))
    } else {
      setSelectedMetalTiers((prev) => [...prev, metalTier])
    }
  }

  return (
    <div className='flex flex-col gap-2'>
      <FiltersDropdown
        align={align}
        open={open}
        onOpenChange={setOpen}
        trigger={trigger}
      >
        <div className='flex flex-col gap-6 p-4 w-96'>
          <CheckboxGroup label='Metal tiers'>
            {METAL_TIER_OPTIONS.map((option) => (
              <Checkbox
                key={option.value}
                value={option.value}
                label={option.label}
                checked={selectedMetalTiers.includes(option.value as MetalTier)}
                onChange={() => onChangeMetalTier(option.value as MetalTier)}
              />
            ))}
          </CheckboxGroup>
          <TextInput
            label='Coverage percentiles'
            description='Enter a comma-separated list of coverage percentiles between 1 and 100'
            value={coveragePercentilesText}
            error={!areCoveragePercentilesValid(coveragePercentilesText) ? 'Coverage percentiles must be between 1 and 100, and separated by commas' : undefined}
            onChange={(e) => {
              const sanitizedValue = e.target.value.replace(/[^0-9,]/g, '')
              e.target.value = sanitizedValue
              handleCoveragePercentilesChange(sanitizedValue)
            }}
          />
          <div className='flex justify-end'>
            <Button
              variant='primary'
              size='large'
              rightIcon={generateQuoteMutation.isPending ? LoadingIndicator : ArrowRight}
              onClick={onClickNext}
              disabled={generateQuoteMutation.isPending || !selectedMetalTiers.length || !selectedCoveragePercentiles.size || !areCoveragePercentilesValid(coveragePercentilesText)}
            >
              Next
            </Button>
          </div>
          {error && (
            <Banner title={error.message} type='critical'>
              {error.errors?.join(', ') ?? 'Please return to the previous step and try again.'}
            </Banner>
          )}
        </div>
      </FiltersDropdown>
    </div>
  )
}
